// Moliya (buxgalteriya) — `xsib-finance` servisi bilan kontrakt.
//
//   GET    /api/finance/accounts?activeOnly        → AccountView[]
//   POST   /api/finance/accounts                   → AccountView (201)
//   PUT    /api/finance/accounts/{id}              → AccountView
//
//   GET    /api/finance/cost-centers?activeOnly    → CostCenterView[]
//
//   GET    /api/finance/periods                    → PeriodView[]
//   POST   /api/finance/periods {code}             → PeriodView (201)
//   DELETE /api/finance/periods/{id}               → (tanali javob, 204 EMAS)
//   POST   /api/finance/periods/{id}/close         → PeriodCloseResult
//
//   POST   /api/finance/expenses                   → JournalEntryView (201)
//   GET    /api/finance/entries?periodId&from&to&kind&accountId&orgNodeId&search&sortField&sortOrder&page&size → ListPage<JournalEntryView>
//   GET    /api/finance/entries/totals?(o'sha filtrlar) → { entries, amount }
//   GET    /api/finance/entries/{id}               → JournalEntryView
//
//   GET    /api/finance/reports/export/{kind}?format&from&to&groupBy
//                                                  → fayl oqimi (CSV/XLSX/JSON)
//
// Huquqlar: hisoblar rejasi `finance.account.view` / `.write`, davrlar
// `finance.period.view` / `.create` / `.close`, xarajat
// `finance.expense.write`.
//
// ⚠️ Kodlar `.view` bilan tugaydi, `.read` bilan EMAS — bu yozuv xatosi
// emas: `identity.seed_system_roles()` `%.read` bilan tugagan huquqni
// "Kuzatuvchi" roliga O'Z-O'ZIDAN qo'shadi, moliya esa maxfiy sxema
// (`shared/auth/permissions.ts` dagi batafsil izoh).
//
// =====================================================================
// ⚠️ 1-QOIDA — MOLIYA LEDGERNI NUSXALAMAYDI (QARORLAR.md §20.2)
// =====================================================================
// Bu yerda abonent balansi ham, trafik ham, xom `entry` ham YO'Q va
// bo'lmaydi. Daromad SAQLANMAYDI: davr yopilganda `ledger` dan BITTA
// agregat so'raladi va u BITTA yig'ma provodkaga aylanadi.
//
// Panel uchun buning oqibati bitta va u ekranda ko'rinishi SHART: bu
// sahifalardagi son `ledger` sahifasidagi jonli sondan FARQ QILISHI
// normal — biri yopilgan davrni, ikkinchisi hozirgi holatni ko'rsatadi.
// Shuning uchun har moliyaviy ko'rinishda `sourceAsOf` ("qaysi vaqt
// holatiga") chiziladi (§18, 3-band). Uni tashlab yuborish rahbarni
// "ikki panelda ikki raqam" holatiga tushirardi va u ikkalasiga ham
// ishonmay qo'yardi.
//
// =====================================================================
// ⚠️ 2-QOIDA — XARAJAT MARKAZI IMKONIYAT BO'YICHA (§19.3)
// =====================================================================
// Ro'yxat `/api/finance/cost-centers` dan KELGAN holida ishlatiladi.
// Panel butun daraxtni olib o'zi filtrlamaydi va `kind === 'BRANCH'`
// KABI SOLISHTIRISH bu faylda ham, uni ishlatadigan oynalarda ham YO'Q:
// server allaqachon `is_cost_center` imkoniyati bo'yicha ajratib bergan
// (`CostCenterController` javadoc'i aynan shu sababni yozadi).
//
// ⚠️ Bu ro'yxat BO'SH bo'lishi mumkin va bu NUQSON EMAS: xarajat markazi
// — ma'lumot ishi. Bo'sh javobga "xato" deb emas, "tashkiliy tuzilmaga
// filial qo'shing" deb javob beriladi (`QARORLAR.md` §12.5).
//
// =====================================================================
// ⚠️ 3-QOIDA — IKKI XIL IDEMPOTENTLIK, ATAYLAB
// =====================================================================
//   davr yopish  — kalit SERVERDA (`PeriodServiceImpl#closeKey`),
//                  panel `Idempotency-Key` YUBORMAYDI;
//   xarajat      — kalit KLIENTDA, sarlavha MAJBURIY.
//
// Farqning sababi backendda yozilgan va u to'g'ri: davrni yopishning
// tabiiy kaliti bor (davrning O'ZI), xarajatniki YO'Q — bir kunda bir
// xil summadagi ikki ijara to'lovi ODATIY hol. Agar davr yopishda kalit
// klientdan kelsa, ikkinchi bosishda brauzer YANGI kalit yuborardi va
// mexanizm bor bo'la turib hech narsani to'smasdi.

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { instantSchema, uuidSchema } from '@/shared/api/schemas'
import { downloadServerExport } from '@/shared/lib/serverExport'
import { listParams, serverPageSchema, toPageResult } from '@/shared/api/serverPage'
import type { PageResult } from '@/shared/ui/table/DataTable'
import type { TableState } from '@/shared/ui/table/tableState'

// --- Hisoblar rejasi --------------------------------------------------------

/**
 * Hisob turi — `account_type_valid` CHECK ining nusxasi.
 *
 * <h2>⚠️ NEGA BU RO'YXAT PANELDA YOZILGAN, SERVERDAN OLINMAYDI</h2>
 * `xsib-finance` da turlar ro'yxatini beradigan endpoint YO'Q va uni
 * qo'shish backendga tegishni talab qilardi. Lekin muhimi boshqa: bu
 * ro'yxat DINAMIK EMAS va bo'lmasligi ham kerak — `AccountDraft`
 * javadoc'i buni ochiq yozadi. Hisob turi buxgalteriyaning asosiy
 * tenglamasi (aktiv = passiv + kapital) va u tashkilotdan tashkilotga
 * o'zgarmaydi.
 *
 * ⚠️ `platform.org_node_type` BILAN CHALKASHTIRMANG: u yerda dinamiklik
 * KERAK edi (tashkilot tuzilmasi har xil), bu yerda esa dinamiklik
 * "foyda hisobotini qurib bo'lmaydi" degani bo'lardi — kod qaysi hisob
 * DAROMAD ekanini bilmasdi. Aynan shuning uchun quyidagi tur bo'yicha
 * filtrlar (`EXPENSE_TYPES`, `PAYMENT_SOURCE_TYPES`) §19.3 ni BUZMAYDI:
 * ular yopiq buxgalteriya turiga qaraydi, administrator yozadigan
 * tashkiliy tur NOMIGA emas.
 *
 * Naqsh yangi emas: `ORG_NODE_STATES`, `PERSON_STATES`, `MOVEMENT_KINDS`
 * ham aynan shunday — bazadagi yopiq CHECK ro'yxatining ko'zgusi.
 */
export const ACCOUNT_TYPES = ['ASSET', 'LIABILITY', 'EQUITY', 'REVENUE', 'EXPENSE'] as const
export type AccountType = (typeof ACCOUNT_TYPES)[number]

/**
 * Xarajat satriga qo'yiladigan hisob turlari.
 *
 * ⚠️ Endi server HAM majburlaydi: xarajat hisobi `EXPENSE` turida
 * bo'lmasa `POST /api/finance/expenses` 400 qaytaradi
 * (`ExpenseServiceImpl.record`, B13/F8). Panel filtri esa qulaylik
 * uchun qoladi — operator DAROMAD hisobini umuman tanlay olmasin.
 * Ilgari faqat panel to'sardi va uni chetlab (to'g'ridan-to'g'ri API)
 * ijarani daromad hisobiga yozib bo'lardi — provodka O'ZGARMAS
 * (`finance.journal_immutable()`), ya'ni keyin tuzatib bo'lmasdi.
 */
export const EXPENSE_TYPES: readonly AccountType[] = ['EXPENSE']

/**
 * Xarajat SINFI — `account_expense_class_valid` CHECK ining nusxasi
 * (`V202608151500`).
 *
 * ⚠️ FAQAT `type = 'EXPENSE'` da ma'noli va u yerda MAJBURIY; qolgan
 * turlarda `null` BO'LISHI SHART — cheklov ikki tomonlama va uni server
 * ham (`AccountDraft`), baza ham (`account_expense_class_matches_type`)
 * majburlaydi.
 *
 * ⚠️ Foyda hisoboti AYNAN shu qiymatga qaraydi, hisob KODIGA emas
 * (§19.3). Ya'ni bu maydon YO'QOLSA hisobot xato bermaydi — jimgina
 * boshqa raqam ko'rsatadi: `COGS` `OPERATING` ga aylansa yalpi foyda
 * daromadga tenglashadi va marja 100% bo'lib ko'rinadi.
 */
export const EXPENSE_CLASSES = ['COGS', 'OPERATING', 'OTHER'] as const

/**
 * To'lov manbai bo'la oladigan hisob turlari.
 *
 * Pul yo mavjud mablag'dan chiqadi (`ASSET` — bank, kassa), yoki hali
 * to'lanmagan bo'lsa majburiyat qoladi (`LIABILITY` — yetkazib
 * beruvchi). Uchinchi variant ikki yozuvli buxgalteriyada yo'q.
 */
export const PAYMENT_SOURCE_TYPES: readonly AccountType[] = ['ASSET', 'LIABILITY']

export const CASH_FLOW_CLASSES = ['CASH', 'OPERATING', 'INVESTING', 'FINANCING'] as const
export type CashFlowClass = (typeof CASH_FLOW_CLASSES)[number]

export const BALANCE_SHEET_TYPES: readonly string[] = ['ASSET', 'LIABILITY', 'EQUITY']

const accountSchema = z.object({
  id: uuidSchema,
  parentId: uuidSchema.nullish(),
  code: z.string(),
  name: z.string(),
  type: z.string(),
  /** Guruh hisobga provodka YOZILMAYDI — tanlash ro'yxatida ko'rsatilmaydi */
  postable: z.boolean(),
  /** Xarajat markazi MAJBURIYmi */
  requiresDimension: z.boolean(),
  active: z.boolean(),
  /** `finance.ledger_map` shunga tayanadi — o'chirib bo'lmaydi */
  system: z.boolean(),
  /**
   * ⚠️ FAQAT `EXPENSE` da to'lgan, qolganlarida javobda UMUMAN yo'q —
   * `trialBalanceRowSchema.expenseClass` bilan aynan bir xil holat va
   * bir xil sabab. `.nullish()` shuning uchun.
   */
  expenseClass: z.string().nullish(),
  cashFlow: z.string().nullish(),
})

export interface Account {
  id: string
  parentId: string | null
  code: string
  name: string
  /**
   * ⚠️ `AccountType` EMAS, `string`. Server yangi tur qo'shsa panel
   * yiqilmasligi kerak: noma'lum tur shunchaki KODI bilan ko'rsatiladi.
   * Yopiq ro'yxat FORMADA ishlatiladi (u yerda tanlov kerak), o'qishda
   * esa serverning so'zi qonun.
   */
  type: string
  postable: boolean
  requiresDimension: boolean
  active: boolean
  system: boolean
  /**
   * ⚠️ `EXPENSE_CLASSES` yopiq ro'yxati EMAS, `string` — `type` bilan aynan bir xil qaror:
   * o'qishda serverning so'zi qonun, yopiq ro'yxat esa FORMADA.
   * Xarajat bo'lmagan hisobda `null`.
   */
  expenseClass: string | null
  cashFlow: string | null
}

function toAccount(raw: z.infer<typeof accountSchema>): Account {
  return {
    id: raw.id,
    parentId: raw.parentId ?? null,
    code: raw.code,
    name: raw.name,
    type: raw.type,
    postable: raw.postable,
    requiresDimension: raw.requiresDimension,
    active: raw.active,
    system: raw.system,
    expenseClass: raw.expenseClass ?? null,
    cashFlow: raw.cashFlow ?? null,
  }
}

/**
 * @param activeOnly xarajat oynasi `true` yuboradi (o'chirilgan hisob
 *                   tanlanmasin), hisoblar rejasi sahifasi esa `false` —
 *                   `AccountController#list` aynan shu taqsimotni yozadi
 */
export async function fetchAccounts(
  activeOnly: boolean,
  signal?: AbortSignal,
): Promise<readonly Account[]> {
  const response = await api.get<unknown>('/api/finance/accounts', {
    ...(signal ? { signal } : {}),
    params: { activeOnly },
  })
  return z.array(accountSchema).parse(response.data).map(toAccount)
}

export interface AccountDraft {
  /** `null` = ildiz hisob */
  parentId: string | null
  /** `[A-Za-z0-9._-]{1,32}` */
  code: string
  name: string
  type: AccountType
  postable: boolean
  /** ⚠️ Guruh hisobda MA'NOSIZ — server ham, baza ham rad etadi */
  requiresDimension: boolean
  active: boolean
  /**
   * `COGS` | `OPERATING` | `OTHER`; xarajat bo'lmagan hisobda `null`.
   *
   * ⚠️ MAYDON MAJBURIY — `undefined` qoldirmang. `PUT` TO'LIQ
   * ALMASHTIRISH: server yuborilmagan sinfni SAQLAMAYDI, `EXPENSE`
   * uchun `OPERATING` ga TUSHIRADI (`AccountDraft` kompakt
   * konstruktori, `DEFAULT_EXPENSE_CLASS`). Jonli o'lchandi
   * (Staging, 2026-08-16): `COGS` hisobning FAQAT NOMI o'zgartirilganda
   * `expense_class` bazada `OPERATING` bo'lib qoldi — ya'ni tahrirlash
   * yalpi foydani jimgina buzardi.
   */
  expenseClass: string | null
  /** Faqat balans hisoblarida; `null` — server shablondan yoki turidan oladi */
  cashFlow: CashFlowClass | null
}

export async function createAccount(draft: AccountDraft): Promise<Account> {
  const response = await api.post<unknown>('/api/finance/accounts', draft)
  return toAccount(accountSchema.parse(response.data))
}

export async function updateAccount(id: string, draft: AccountDraft): Promise<Account> {
  const response = await api.put<unknown>(`/api/finance/accounts/${id}`, draft)
  return toAccount(accountSchema.parse(response.data))
}

export interface AccountTreeNode extends Account {
  children: AccountTreeNode[]
}

/**
 * Yassi ro'yxatdan hisoblar daraxti.
 *
 * ⚠️ Otasi to'plamda BO'LMAGAN hisob ILDIZ sifatida qaytadi. Bu
 * `activeOnly=true` da haqiqatan bo'ladigan holat: guruh hisob
 * yopilgan, bolalari esa ochiq. Bunday qatorni tashlab yuborish
 * hisoblarni ekrandan JIMGINA yo'qotardi (`buildOrgTree` bilan bir xil
 * qaror va bir xil sabab).
 *
 * ⚠️ Chuqurlik cheklanmagan — funksiya qavatlarni SANAMAYDI.
 */
export function buildAccountTree(accounts: readonly Account[]): AccountTreeNode[] {
  const byId = new Map<string, AccountTreeNode>()
  for (const account of accounts) {
    byId.set(account.id, { ...account, children: [] })
  }

  const roots: AccountTreeNode[] = []
  for (const account of accounts) {
    const built = byId.get(account.id)
    if (!built) continue
    const parent = account.parentId === null ? undefined : byId.get(account.parentId)
    if (parent) parent.children.push(built)
    else roots.push(built)
  }
  return roots
}

/**
 * Hisobning butun avlodi — ota tanlashda SIKLNI to'sadi.
 *
 * Bazada `account_not_self_parent` faqat o'z-o'ziga bog'lashni to'sadi;
 * uzun halqa (A → B → A) esa cheklovdan o'tib ketardi va hisoblar rejasi
 * ekranda YARIM YO'QOLARDI — halqadagi shox ildizga chiqmaydi.
 */
export function collectAccountSubtree(
  accounts: readonly Account[],
  rootId: string,
): ReadonlySet<string> {
  const childrenOf = new Map<string, string[]>()
  for (const account of accounts) {
    if (account.parentId === null) continue
    const list = childrenOf.get(account.parentId)
    if (list) list.push(account.id)
    else childrenOf.set(account.parentId, [account.id])
  }

  const result = new Set<string>([rootId])
  const queue = [rootId]
  while (queue.length > 0) {
    const current = queue.pop()
    if (current === undefined) break
    for (const child of childrenOf.get(current) ?? []) {
      if (result.has(child)) continue
      result.add(child)
      queue.push(child)
    }
  }
  return result
}

// --- Xarajat markazlari -----------------------------------------------------

const costCenterSchema = z.object({
  id: uuidSchema,
  parentId: uuidSchema.nullish(),
  /** Materiallashgan `ltree` yo'li, matn ko'rinishida */
  path: z.string(),
  code: z.string(),
  displayName: z.string(),
  kind: z.string(),
  state: z.string(),
})

export interface CostCenter {
  id: string
  parentId: string | null
  path: string
  code: string
  displayName: string
  /**
   * ⚠️ Tashkiliy tur KODI — ekranda KO'RSATILADI, lekin hech qachon
   * SOLISHTIRILMAYDI (§19.3). "Bu xarajat markazimi?" degan savolga
   * server allaqachon javob bergan: ro'yxatda bo'lishining o'zi javob.
   */
  kind: string
  state: string
}

function toCostCenter(raw: z.infer<typeof costCenterSchema>): CostCenter {
  return {
    id: raw.id,
    parentId: raw.parentId ?? null,
    path: raw.path,
    code: raw.code,
    displayName: raw.displayName,
    kind: raw.kind,
    state: raw.state,
  }
}

export async function fetchCostCenters(signal?: AbortSignal): Promise<readonly CostCenter[]> {
  const response = await api.get<unknown>('/api/finance/cost-centers', {
    ...(signal ? { signal } : {}),
    params: { activeOnly: true },
  })
  return z.array(costCenterSchema).parse(response.data).map(toCostCenter)
}

/**
 * Har tugunning KO'RINADIGAN chuqurligi: `id` → qavat.
 *
 * ⚠️ Iyerarxiya `path` dan quriladi, `parentId` bo'ylab YURIB emas —
 * `QARORLAR.md` §20.4 ("daraxt bitta egada") va `CostCenterView.path`
 * javadoc'i. Sabab amaliy: `SUBTREE` doirasidagi operator daraxtning
 * FAQAT o'z shoxini oladi va uning ildizining otasi ro'yxatda YO'Q —
 * `parentId` bo'ylab yurgan kod o'sha yerda uzilib qolardi.
 *
 * ⚠️ Chuqurlik ABSOLYUT emas, RO'YXATGA NISBATAN: eng yuqori qavat 0
 * deb olinadi. Aks holda filial boshlig'ining ekranida butun daraxt
 * o'ngga surilib ketardi — u ko'rmaydigan bosh ofis qavatlari uchun.
 *
 * ⚠️ Bitta yurish (`O(n)`): xarita bir marta quriladi. Har tugun uchun
 * alohida hisoblash `O(n²)` bo'lardi va u faqat daraxt kattalashganda
 * sezilardi.
 */
export function costCenterDepths(all: readonly CostCenter[]): ReadonlyMap<string, number> {
  const labels = (path: string) => path.split('.').filter((label) => label !== '').length

  let top = Number.MAX_SAFE_INTEGER
  for (const node of all) top = Math.min(top, labels(node.path))

  const depths = new Map<string, number>()
  for (const node of all) depths.set(node.id, Math.max(labels(node.path) - top, 0))
  return depths
}

// --- Filial hisoboti (jamlovchi P&L) ----------------------------------------
//
// `GET /api/finance/reports/branches` — mijoz hujjatining §5.5 bandlari
// "Filiallarni taqqoslash", "Kompaniya KPI" va "Bosh ofis jamlovchi
// ko'rinishi" AYNI so'rovdan chiqadi (`FinanceReportController#branches`).
//
// ⚠️ QATORLARNI PASTGA QO'SHMANG — javob DARAXT bo'ylab yig'ilgan
// (`docs/11` §3): ota qator bolasini QAMRAB oladi, ya'ni `rows` ni
// yig'sangiz filiallar ikki marta sanaladi. Umumiy son uchun `company`
// bloki bor. `unallocated` — hech qaysi filialga biriktirilmagan qism;
// u ATAYLAB alohida ko'rsatiladi, nolga aylantirilmaydi (§5.6).
//
// ⚠️ Sonlar MINOR birlikda (`*Minor`) va `money(minor, currency)` bilan
// o'raladi — `note.currency` butun javob uchun bitta.

/**
 * Har moliyaviy hisobotning MAJBURIY sarlavhasi — `ReportScopeNote`
 * (`QARORLAR.md` §18, 3-band: «har moliyaviy hisobotda holat
 * sanasi/vaqti ko'rsatilsin»).
 *
 * ⚠️ BITTA JOYDA: to'rtta hisobot (`branches`, `income-statement`,
 * `trial-balance`, `cost-centers`) AYNAN shu sarlavhani qaytaradi.
 * Har sahifada qayta yozilsa, birontasida `scopeRestricted` tushib
 * qolardi va o'sha ekranda doira ogohlantirishi JIMGINA yo'qolardi.
 */
const reportScopeNoteSchema = z.object({
  from: z.string(),
  toInclusive: z.string(),
  currency: z.string(),
  timezone: z.string(),
  periods: z.array(z.object({ code: z.string(), state: z.string() })).default([]),
  scopeRestricted: z.boolean().default(false),
  generatedAt: z.string(),
})

export type ReportScopeNote = z.infer<typeof reportScopeNoteSchema>

const branchAggregateSchema = z.object({
  revenueMinor: z.number(),
  cogsMinor: z.number(),
  operatingExpenseMinor: z.number(),
  otherExpenseMinor: z.number(),
  netProfitMinor: z.number(),
  previousRevenueMinor: z.number(),
  previousNetProfitMinor: z.number(),
  lineCount: z.number(),
})

const branchRowSchema = branchAggregateSchema.extend({
  nodeId: uuidSchema,
  nodeCode: z.string(),
  nodeName: z.string(),
  nodeKind: z.string(),
  depth: z.number(),
})

// ⚠️ Eksport qilinadi — `tests/finance-branch-report.mjs` uni jonli
// javob SHAKLIga qarshi ishlatadi (backend maydon driftini ushlash uchun).
export const branchReportSchema = z.object({
  note: reportScopeNoteSchema,
  rows: z.array(branchRowSchema).default([]),
  unallocated: branchAggregateSchema,
  company: branchAggregateSchema,
  costCenterCount: z.number(),
  truncated: z.boolean().default(false),
})

export type BranchAggregate = z.infer<typeof branchAggregateSchema>
export type BranchReportRow = z.infer<typeof branchRowSchema>
export type BranchReport = z.infer<typeof branchReportSchema>

export interface BranchReportRange {
  /** `YYYY-MM-DD`, ikkala chegara ham KIRADI; ikkalasi ham ixtiyoriy */
  from?: string
  to?: string
}

export async function fetchBranchReport(
  range: BranchReportRange,
  signal?: AbortSignal,
): Promise<BranchReport> {
  const response = await api.get<unknown>('/api/finance/reports/branches', {
    ...(signal ? { signal } : {}),
    params: { from: range.from, to: range.to },
  })
  return branchReportSchema.parse(response.data)
}

// --- Foyda hisoboti (P&L) ---------------------------------------------------
//
// `GET /api/finance/reports/income-statement` — mijoz hujjatining §5.3
// OLTITA «foyda» bandi BITTA so'rovda: yalpi daromad, tannarx, yalpi
// foyda, operatsion xarajat, sof foyda, marja.
//
// ⚠️ Panel bu sonlarni QAYTA HISOBLAMAYDI. To'rttasi qolgan ikkitasidan
// arifmetik chiqadi, lekin arifmetikaning yagona joyi — serverdagi
// `IncomeStatement#of`. Panelda takrorlansa, bir kuni ikkisi ajralib
// ketardi va ekranda o'ziga zid hisobot turardi (`QARORLAR.md` §12.2).
//
// ⚠️ Bu hisobot ham panelda faqat EKSPORT sifatida bor edi
// (`FINANCE_EXPORT_KINDS` da `income-statement`).

const incomeEliminationsSchema = z.object({
  internalSalesMinor: z.number(),
  internalCostMinor: z.number(),
  markupChangeMinor: z.number(),
  markupAsOf: z.string().nullish(),
})

/**
 * ⚠️ MARJALAR `.nullish()` — VA BU O'LCHANGAN, TAXMIN EMAS.
 *
 * Jonli Staging (2026-08-14): daromadi nol bo'lgan oynada javob
 * `grossMarginPercent`/`netMarginPercent` maydonlarini UMUMAN
 * yubormaydi (server `null` beradi, Jackson uni tashlaydi). Daromadli
 * oynada esa ular son bo'lib keladi (`100.0`).
 *
 * ⚠️ `null` ni `0` ga aylantirmang: «marja 0%» MA'NOLI javob bo'lardi,
 * holbuki savol umuman qo'yilmagan — bo'linuvchi nol
 * (`IncomeStatement#margin` javadoc'i). Panel «—» chizadi.
 *
 * ⚠️ Eksport qilinadi — `tests/finance-statements.mjs` uni JONLI javob
 * shakliga qarshi ishlatadi.
 */
export const incomeStatementSchema = z.object({
  note: reportScopeNoteSchema,
  revenueMinor: z.number(),
  cogsMinor: z.number(),
  grossProfitMinor: z.number(),
  operatingExpenseMinor: z.number(),
  otherExpenseMinor: z.number(),
  netProfitMinor: z.number(),
  grossMarginPercent: z.number().nullish(),
  netMarginPercent: z.number().nullish(),
  /**
   * Foyda hisobotiga TUSHMAGAN, lekin oynada harakat qilgan summa
   * (bank, depozit — har provodkaning ikkinchi satri).
   *
   * ⚠️ Nol bo'lmasligi ODATIY va u YASHIRILMAYDI: busiz «hamma pul
   * harakati hisobga olindi» degan yolg'on taassurot paydo bo'lardi.
   */
  unclassifiedMinor: z.number(),
  nodeId: uuidSchema.nullish(),
  consolidated: z.boolean().default(false),
  eliminations: incomeEliminationsSchema.nullish(),
})

export type IncomeStatement = z.infer<typeof incomeStatementSchema>

export interface StatementQuery extends BranchReportRange {
  /** Xarajat markazi; bo'sh — butun kompaniya */
  nodeId?: string | null
}

function statementParams(query: StatementQuery) {
  return { from: query.from, to: query.to, nodeId: query.nodeId ?? undefined }
}

export async function fetchIncomeStatement(
  query: StatementQuery,
  signal?: AbortSignal,
): Promise<IncomeStatement> {
  const response = await api.get<unknown>('/api/finance/reports/income-statement', {
    ...(signal ? { signal } : {}),
    params: statementParams(query),
  })
  return incomeStatementSchema.parse(response.data)
}

// --- Aylanma-qoldiq vedomosti (trial balance) -------------------------------
//
// `GET /api/finance/reports/trial-balance` — mijozning «ERP ga
// uzatiladigan tuzilgan buxgalteriya shakli» talabi. Har ERP (1C, SAP,
// Odoo, QuickBooks) aynan shu shaklni import qiladi.
//
// ⚠️ `balanced` ni `note.scopeRestricted` BILAN BIRGA o'qing
// (`TrialBalance#balanced` javadoc'i):
//   · to'liq doirada `false` — MA'LUMOT BUZILGAN (xato);
//   · tor doirada  `false` — ODATIY (provodkaning ikkinchi satri
//     doiraga tushmaydi).
// Ikkalasini bir xil ko'rsatish buxgalterni «buxgalteriyam buzuq»
// degan noto'g'ri xulosaga olib kelardi.

const trialBalanceRowSchema = z.object({
  accountId: uuidSchema,
  accountCode: z.string(),
  accountName: z.string(),
  /** ERKIN SATR — server yangi hisob turi qo'shsa sahifa o'chmasin */
  accountType: z.string(),
  /**
   * ⚠️ FAQAT `EXPENSE` hisoblarda to'ldirilgan, qolganlarida javobda
   * UMUMAN yo'q (o'lchandi: jonli javobda `ASSET`/`LIABILITY`/`REVENUE`
   * qatorlarida bu kalit yo'q).
   */
  expenseClass: z.string().nullish(),
  debitMinor: z.number(),
  creditMinor: z.number(),
  /** `credit − debit`. Daromad va passivda musbat, aktiv/xarajatda manfiy */
  netMinor: z.number(),
  lineCount: z.number(),
})

/** ⚠️ Eksport qilinadi — jonli javob shakliga qarshi ishlatiladi */
export const trialBalanceSchema = z.object({
  note: reportScopeNoteSchema,
  rows: z.array(trialBalanceRowSchema).default([]),
  totalDebitMinor: z.number(),
  totalCreditMinor: z.number(),
  balanced: z.boolean(),
  /**
   * ⚠️ Alohida maydon: kesilgan ro'yxatdan hisoblangan yig'indi
   * haqiqiydan kam chiqadi va buni boshqa hech narsa ko'rsatmasdi.
   */
  truncated: z.boolean().default(false),
})

export type TrialBalanceRow = z.infer<typeof trialBalanceRowSchema>
export type TrialBalance = z.infer<typeof trialBalanceSchema>

export async function fetchTrialBalance(
  query: StatementQuery,
  signal?: AbortSignal,
): Promise<TrialBalance> {
  const response = await api.get<unknown>('/api/finance/reports/trial-balance', {
    ...(signal ? { signal } : {}),
    params: statementParams(query),
  })
  return trialBalanceSchema.parse(response.data)
}

// --- Balans va pul oqimi (ADR-043, 3-bosqich) -------------------------------

const balanceLineSchema = z.object({
  accountId: uuidSchema,
  code: z.string(),
  name: z.string(),
  amountMinor: z.number(),
})

const interofficeSchema = z.object({
  postedMinor: z.number(),
  derivedMinor: z.number(),
  totalMinor: z.number(),
})

const balanceEliminationsSchema = z.object({
  interofficeMinor: z.number(),
  markupMinor: z.number(),
  markupAsOf: z.string().nullish(),
})

export const balanceSheetSchema = z.object({
  note: reportScopeNoteSchema,
  asOf: z.string(),
  nodeId: uuidSchema.nullish(),
  consolidated: z.boolean(),
  assets: z.array(balanceLineSchema).default([]),
  liabilities: z.array(balanceLineSchema).default([]),
  equity: z.array(balanceLineSchema).default([]),
  currentEarningsMinor: z.number(),
  interoffice: interofficeSchema.nullish(),
  eliminations: balanceEliminationsSchema.nullish(),
  totalAssetsMinor: z.number(),
  totalLiabilitiesMinor: z.number(),
  totalEquityMinor: z.number(),
  balanced: z.boolean(),
  stakeholderDebtMinor: z.number(),
})

export type BalanceLine = z.infer<typeof balanceLineSchema>
export type BalanceSheet = z.infer<typeof balanceSheetSchema>

export async function fetchBalanceSheet(
  query: { asOf: string; nodeId?: string | null },
  signal?: AbortSignal,
): Promise<BalanceSheet> {
  const response = await api.get<unknown>('/api/finance/reports/balance-sheet', {
    ...(signal ? { signal } : {}),
    params: { asOf: query.asOf, nodeId: query.nodeId ?? undefined },
  })
  return balanceSheetSchema.parse(response.data)
}

/** Sintetik qatorlar: konsolidatsiyadagi ustama o'zgarishi va markazning bosh ofis bilan hisobi */
export const CASH_FLOW_SYNTHETIC_KEYS = ['MARKUP', 'INTEROFFICE'] as const

const cashFlowLineSchema = z.object({
  key: z.string(),
  accountId: uuidSchema.nullish(),
  code: z.string().nullish(),
  name: z.string(),
  amountMinor: z.number(),
})

export const cashFlowSchema = z.object({
  note: reportScopeNoteSchema,
  nodeId: uuidSchema.nullish(),
  consolidated: z.boolean(),
  netProfitMinor: z.number(),
  operating: z.array(cashFlowLineSchema).default([]),
  operatingTotalMinor: z.number(),
  investing: z.array(cashFlowLineSchema).default([]),
  investingTotalMinor: z.number(),
  financing: z.array(cashFlowLineSchema).default([]),
  financingTotalMinor: z.number(),
  netChangeMinor: z.number(),
  cashOpeningMinor: z.number(),
  cashClosingMinor: z.number(),
  reconciled: z.boolean(),
})

export type CashFlowLine = z.infer<typeof cashFlowLineSchema>
export type CashFlow = z.infer<typeof cashFlowSchema>

export async function fetchCashFlow(query: StatementQuery, signal?: AbortSignal): Promise<CashFlow> {
  const response = await api.get<unknown>('/api/finance/reports/cash-flow', {
    ...(signal ? { signal } : {}),
    params: statementParams(query),
  })
  return cashFlowSchema.parse(response.data)
}

// --- Moliyaviy davrlar ------------------------------------------------------

/** `period_state_valid` CHECK ining nusxasi */
export const PERIOD_STATES = ['OPEN', 'CLOSED'] as const

const periodSchema = z.object({
  id: uuidSchema,
  /** `YYYY-MM` */
  code: z.string(),
  startsOn: z.string(),
  /** ⚠️ CHEGARADAN TASHQARI (exclusive): avgust uchun `2026-09-01` */
  endsOn: z.string(),
  startsAt: instantSchema,
  /** ⚠️ exclusive. `Asia/Kabul` da avgust `2026-07-31T19:30:00Z` da boshlanadi */
  endsAt: instantSchema,
  state: z.string(),
  closedAt: instantSchema.nullish(),
  closedBy: z.string().nullish(),
})

export interface Period {
  id: string
  code: string
  startsOn: string
  /** ⚠️ EXCLUSIVE — ekranda KO'RSATISHDAN oldin bir kun ayiriladi */
  endsOn: string
  startsAt: string
  endsAt: string
  state: string
  closedAt: string | null
  closedBy: string | null
}

function toPeriod(raw: z.infer<typeof periodSchema>): Period {
  return {
    id: raw.id,
    code: raw.code,
    startsOn: raw.startsOn,
    endsOn: raw.endsOn,
    startsAt: raw.startsAt,
    endsAt: raw.endsAt,
    state: raw.state,
    closedAt: raw.closedAt ?? null,
    closedBy: raw.closedBy ?? null,
  }
}

export function isPeriodClosed(period: Period): boolean {
  return period.state === 'CLOSED'
}

export async function fetchPeriods(signal?: AbortSignal): Promise<readonly Period[]> {
  const response = await api.get<unknown>('/api/finance/periods', {
    ...(signal ? { signal } : {}),
  })
  return z.array(periodSchema).parse(response.data).map(toPeriod)
}

/** @param code `YYYY-MM`. Chegaralar SERVERDA, tenant zonasida hisoblanadi */
export async function createPeriod(code: string): Promise<Period> {
  const response = await api.post<unknown>('/api/finance/periods', { code })
  return toPeriod(periodSchema.parse(response.data))
}

/** ⚠️ Faqat OCHIQ va PROVODKASIZ davr o'chiriladi — aks holda server rad etadi */
export async function deletePeriod(id: string): Promise<void> {
  await api.delete<unknown>(`/api/finance/periods/${id}`)
}

const periodCloseResultSchema = z.object({
  period: periodSchema,
  /** Yozilgan yig'ma provodka; harakatsiz davrda `null` */
  entryId: uuidSchema.nullish(),
  lineCount: z.number(),
  sourceRows: z.number(),
  /** ⚠️ `true` — davr ALLAQACHON yopilgan edi, yangi provodka YOZILMADI */
  replayed: z.boolean(),
  /** Ledger qaysi lahzadagi holatni bergan */
  sourceAsOf: instantSchema.nullish(),
  message: z.string(),
})

export interface PeriodCloseResult {
  period: Period
  entryId: string | null
  lineCount: number
  sourceRows: number
  replayed: boolean
  sourceAsOf: string | null
  /** Server matni — YO'QOTILMAYDI, u yagona aniq tafsilotni tashiydi */
  message: string
}

/**
 * <b>Davrni yopish</b> — pul YOZADI va QAYTARIB BO'LMAYDI.
 *
 * ⚠️ `Idempotency-Key` sarlavhasi ATAYLAB YUBORILMAYDI: kalit serverda,
 * davrning o'z id'sidan quriladi (`PeriodServiceImpl#closeKey`). Bu
 * yerga sarlavha qo'shilsa, ikkinchi bosishda brauzer YANGI kalit
 * yuborardi va §20.9-D dagi aynan nosozlik qolardi — "ikki bosish, ikki
 * provodka".
 *
 * ⚠️ Takroriy chaqiruv XATO EMAS: javob `200` va `replayed: true`. Uni
 * oynada YASHIRMANG — operator "yopdim" bilan "allaqachon yopilgan edi"
 * ni ajrata olishi kerak (`CLAUDE.md` §3.2, 4-qoida).
 */
export async function closePeriod(id: string): Promise<PeriodCloseResult> {
  const response = await api.post<unknown>(`/api/finance/periods/${id}/close`)
  const raw = periodCloseResultSchema.parse(response.data)
  return {
    period: toPeriod(raw.period),
    entryId: raw.entryId ?? null,
    lineCount: raw.lineCount,
    sourceRows: raw.sourceRows,
    replayed: raw.replayed,
    sourceAsOf: raw.sourceAsOf ?? null,
    message: raw.message,
  }
}

// --- Provodkalar jurnali ----------------------------------------------------

const entryLineSchema = z.object({
  id: uuidSchema,
  lineNo: z.number(),
  accountId: uuidSchema,
  accountCode: z.string(),
  accountName: z.string(),
  orgNodeId: uuidSchema.nullish(),
  orgNodeCode: z.string().nullish(),
  orgNodeName: z.string().nullish(),
  /** MINOR birlikda; musbat = KREDIT, manfiy = DEBIT */
  amount: z.number(),
  memo: z.string().nullish(),
})

const journalEntrySchema = z.object({
  id: uuidSchema,
  periodId: uuidSchema,
  periodCode: z.string(),
  kind: z.string(),
  entryDate: z.string(),
  currency: z.string(),
  memo: z.string().nullish(),
  source: z.string(),
  sourceAsOf: instantSchema.nullish(),
  reversesId: uuidSchema.nullish(),
  createdBy: z.string().nullish(),
  createdAt: instantSchema,
  amount: z.number(),
  lines: z.array(entryLineSchema),
})

export interface JournalEntryLine {
  id: string
  lineNo: number
  accountId: string
  accountCode: string
  accountName: string
  /** `null` = TAQSIMLANMAGAN (butun tashkilot) */
  orgNodeId: string | null
  orgNodeCode: string | null
  orgNodeName: string | null
  /**
   * MINOR birlikda. ⚠️ ISHORA KELISHUVI `ledger` bilan AYNAN BIR XIL
   * (`docs/02-LEDGER.md` §7): musbat = KREDIT, manfiy = DEBIT. Panel uni
   * AG'DARMAYDI — ag'darish bir kuni bir joyda unutilib, daromadni
   * xarajat qilib ko'rsatardi.
   */
  amount: number
  memo: string | null
}

export interface JournalEntry {
  id: string
  periodId: string
  periodCode: string
  kind: string
  entryDate: string
  currency: string
  memo: string | null
  source: string
  /** ⚠️ "Qaysi vaqt holatiga" — §18, 3-band. `MANUAL` provodkada `null` */
  sourceAsOf: string | null
  reversesId: string | null
  createdBy: string | null
  createdAt: string
  /** Provodka summasi, MINOR birlikda — kredit satrlari yig'indisi (debetga teng) */
  amount: number
  lines: readonly JournalEntryLine[]
}

function toEntry(raw: z.infer<typeof journalEntrySchema>): JournalEntry {
  return {
    id: raw.id,
    periodId: raw.periodId,
    periodCode: raw.periodCode,
    kind: raw.kind,
    entryDate: raw.entryDate,
    currency: raw.currency,
    memo: raw.memo ?? null,
    source: raw.source,
    sourceAsOf: raw.sourceAsOf ?? null,
    reversesId: raw.reversesId ?? null,
    createdBy: raw.createdBy ?? null,
    createdAt: raw.createdAt,
    amount: raw.amount,
    lines: raw.lines.map((line) => ({
      id: line.id,
      lineNo: line.lineNo,
      accountId: line.accountId,
      accountCode: line.accountCode,
      accountName: line.accountName,
      orgNodeId: line.orgNodeId ?? null,
      orgNodeCode: line.orgNodeCode ?? null,
      orgNodeName: line.orgNodeName ?? null,
      amount: line.amount,
      memo: line.memo ?? null,
    })),
  }
}

export const JOURNAL_FILTER_KEYS = ['periodId', 'from', 'to', 'kind', 'accountId', 'orgNodeId'] as const
export type JournalFilterKey = (typeof JOURNAL_FILTER_KEYS)[number]

const JOURNAL_SORT_FIELDS: Readonly<Record<string, string>> = {
  entryDate: 'ENTRY_DATE',
  amount: 'AMOUNT',
  createdAt: 'CREATED_AT',
}

function journalFilterParams(state: TableState): Record<string, string> {
  const params: Record<string, string> = {}
  for (const key of JOURNAL_FILTER_KEYS) {
    const value = state.filters[key]?.[0]
    if (value) params[key] = value
  }
  const search = state.search.trim()
  if (search) params['search'] = search
  return params
}

/** Provodkalar jurnali — filtr, saralash va server bergan jami son bilan */
export async function fetchJournalEntries(state: TableState, signal?: AbortSignal): Promise<PageResult<JournalEntry>> {
  const response = await api.get<unknown>('/api/finance/entries', {
    ...(signal ? { signal } : {}),
    params: { ...listParams(state, { sortFields: JOURNAL_SORT_FIELDS }), ...journalFilterParams(state) },
  })
  const parsed = serverPageSchema(journalEntrySchema).parse(response.data)
  return toPageResult(parsed, parsed.rows.map(toEntry))
}

const journalTotalsSchema = z.object({ entries: z.number(), amount: z.number(), currency: z.string().nullish() })

export type JournalTotals = z.infer<typeof journalTotalsSchema>

/** Filtrdagi HAMMA provodka soni va summasi — sahifa va saralashga bog'liq emas */
export async function fetchJournalTotals(state: TableState, signal?: AbortSignal): Promise<JournalTotals> {
  const response = await api.get<unknown>('/api/finance/entries/totals', {
    ...(signal ? { signal } : {}),
    params: journalFilterParams(state),
  })
  return journalTotalsSchema.parse(response.data)
}

/**
 * Bitta provodka.
 *
 * ⚠️ Doira rad etsa server `404` beradi, `403` EMAS — provodkaning
 * BORLIGI oshkor bo'lmasligi uchun (`JournalController` javadoc'i).
 * Chaqiruvchi buni "yo'q" deb emas, "ko'rsatib bo'lmadi" deb ko'rsatishi
 * kerak: davr yopishning yig'ma provodkasi taqsimlanmagan satrlardan
 * iborat va u `SUBTREE` doirasidagi operatorga KO'RINMAYDI.
 */
export async function fetchJournalEntry(
  id: string,
  signal?: AbortSignal,
): Promise<JournalEntry> {
  const response = await api.get<unknown>(`/api/finance/entries/${id}`, {
    ...(signal ? { signal } : {}),
  })
  return toEntry(journalEntrySchema.parse(response.data))
}

// --- Xarajat ----------------------------------------------------------------

export interface ExpenseDraft {
  /** `YYYY-MM-DD`. ⚠️ DAVR SERVERDA topiladi — klient uni tanlamaydi */
  entryDate: string
  expenseAccountId: string
  /** Pul QAYERDAN chiqdi — bank, kassa yoki yetkazib beruvchiga majburiyat */
  creditAccountId: string
  /** Xarajat markazi; `null` = taqsimlanmagan */
  orgNodeId: string | null
  /**
   * ⚠️ MINOR birlikda va MUSBAT. Ishorani SERVIS qo'yadi (xarajatga
   * debit, manbaga kredit) — klientga qoldirish "manfiy xarajat" degan
   * yozuvlarga yo'l ochardi.
   */
  amountMinor: number
  memo: string | null
}

/**
 * Xarajat kiritish.
 *
 * ⚠️ `Idempotency-Key` MAJBURIY va u OYNA OCHILGANDA bir marta
 * yasaladi, har bosishda EMAS (`shared/lib/idempotency.ts`). Har
 * bosishda yangi kalit yuborilsa mexanizm bor bo'la turib hech narsani
 * to'smasdi — bu §20.9-D ning aynan ogohlantirishi.
 */
export async function recordExpense(
  draft: ExpenseDraft,
  idempotencyKey: string,
): Promise<JournalEntry> {
  const response = await api.post<unknown>('/api/finance/expenses', draft, {
    headers: { 'Idempotency-Key': idempotencyKey },
  })
  return toEntry(journalEntrySchema.parse(response.data))
}

// ---------------------------------------------------------------------------
// MOLIYAVIY HISOBOT EKSPORTI — `GET /api/finance/reports/export/{kind}`
//
// ⚠️ Bu endpointlar backendda BOR edi, panel esa ularga UMUMAN
// murojaat qilmasdi. Ya'ni buxgalter aylanma qaydnomani yoki foyda va
// zarar hisobotini panel orqali ola olmasdi — faylni faqat qo'lda,
// `curl` bilan olish mumkin edi.
//
// ⚠️ IKKI HUQUQ BIRGA talab qilinadi (`@RequiresPermission` AND bilan
// ishlaydi): `finance.account.view` VA `report.export`. Shuning uchun
// tugma ikkalasi ham bo'lganda chiziladi — bittasi bilan chizilsa
// operator 403 ga urilardi va sababini bilmasdi.
//
// ⚠️ Doira va valyuta SERVERDA hal qilinadi va fayl ichida izoh qatori
// bilan yoziladi. Panel ularni qayta hisoblamaydi.
// ---------------------------------------------------------------------------

/** Backend `FinanceExportKind.slug()` bilan AYNAN bir xil qiymatlar */
export const FINANCE_EXPORT_KINDS = [
  'trial-balance',
  'income-statement',
  'expenses',
  'cost-centers',
  'branches',
  'balance-sheet',
  'cash-flow',
] as const

export type FinanceExportKind = (typeof FINANCE_EXPORT_KINDS)[number]

/** `expenses` uchun guruhlash — serverdagi `ExpenseGrouping` enum'i */
export const EXPENSE_GROUPINGS = ['ORG_NODE', 'ACCOUNT', 'CLASS', 'MONTH'] as const
export type ExpenseGrouping = (typeof EXPENSE_GROUPINGS)[number]

export interface FinanceExportParams {
  /** `YYYY-MM-DD`, ikkala chegara ham KIRADI */
  from?: string
  to?: string
  asOf?: string
  nodeId?: string | null
  groupBy?: ExpenseGrouping
  onProgress?: (loadedBytes: number) => void
}

export async function downloadFinanceExport(
  kind: FinanceExportKind,
  format: string,
  params: FinanceExportParams,
): Promise<void> {
  await downloadServerExport({
    path: `/api/finance/reports/export/${kind}`,
    format,
    params: {
      from: params.from,
      to: params.to,
      asOf: params.asOf,
      nodeId: params.nodeId,
      groupBy: params.groupBy,
    },
    fallbackBaseName: exportBaseName(kind, params),
    ...(params.onProgress ? { onProgress: params.onProgress } : {}),
  })
}

function exportBaseName(kind: FinanceExportKind, params: FinanceExportParams): string {
  if (params.asOf) return `${kind}-${params.asOf}`
  return params.from && params.to ? `${kind}-${params.from}_${params.to}` : kind
}
